<!-- MenuView.vue -->
<script setup lang="ts">
import type { MessageSchema } from '@/i18n';
import { usePostitsStore } from '@/stores/postitsStore';
import type { Postit } from '@/types/planning';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const props = defineProps<{
  date: Date;
  postit: Postit;
}>();

const router = useRouter();
const postitsStore = ref(usePostitsStore());

// const isSelected = computed(() => {
//   return postitsStore.value.selectedPostit?.id === props.postit.id;
// });

function goToPostitDetails() {
  router.push(`/employee/postits/${props.postit.id}`);
}

function handleClickPostit() {
  postitsStore.value.selectedPostit = props.postit;
  goToPostitDetails();
}

// const assignedUser = computed(() => {
//   const id = props.postit.restaurant_user_ids?.[0];
//   if (!id) return null;
//   return employeeStore.restaurantUsers.find(u => u.restaurant_user_id === id) || null;
// });

const sortedTasks = computed(() => {
  if (!props.postit.tasks) return [];
  return [...props.postit.tasks].sort((a, b) => {
    if (a.is_done === b.is_done) return 0;
    return a.is_done ? 1 : -1;
  });
});

const firstUser = computed(() => props.postit.restaurant_users?.[0] || null);
</script>

<template>
  <!-- Placeholder card for optimistic UI -->
  <div
    v-if="props.postit.tempId"
    class="relative mx-2 my-4 flex h-56 w-44 flex-grow items-start rounded-xl bg-gray-200 px-2 py-8 opacity-50 shadow-clickable-card"
  >
    <div
      class="absolute -top-4 left-1/2 max-w-36 -translate-x-1/2 overflow-hidden truncate text-ellipsis whitespace-nowrap rounded-md bg-gray-300 px-4 py-2 text-center font-bold text-xs text-black"
    >
      <p class="h-4 w-20"></p>
    </div>
    <div class="flex flex-col justify-around gap-3 text-sm"></div>
  </div>
  <!-- Actual postit card -->
  <div v-else class="relative py-2">
    <div
      class="relative my-4 flex h-56 w-44 flex-grow cursor-pointer items-start rounded-xl border-[1.5px] border-primary-500 px-2 py-8 shadow-clickable-card"
      :class="{
        'bg-primary-100': !props.postit.is_done,
        'bg-primary-200 opacity-50': props.postit.is_done,
      }"
      @click="handleClickPostit"
    >
      <div
        :class="[
          'absolute -top-4 left-1/2 max-w-36 -translate-x-1/2 whitespace-nowrap rounded-md px-4 py-2 text-center text-xs shadow-md',
          firstUser ? 'bg-primary-500 font-bold text-white' : 'border border-primary-700 bg-primary-100 italic text-primary-700',
        ]"
      >
        <div v-if="firstUser" class="overflow-hidden text-ellipsis">
          {{ firstUser ? `${firstUser.first_name} ${firstUser.last_name}` : '' }}
        </div>
        <div v-else>
          {{ t('employee.postits.unassigned') }}
        </div>
        <div v-if="firstUser" class="absolute -right-2 -top-2 flex items-center justify-center">
          <span
            v-for="(pos, idx) in firstUser.positions"
            :key="pos.id"
            class="block h-4 w-4 rounded-full border border-white"
            :class="{ '-ml-2': idx !== 0 }"
            :style="{ backgroundColor: '#' + pos.color }"
            :title="pos.name"
          ></span>
          <!-- <span class="z-10 block w-4 h-4 bg-blue-700 rounded-full"></span>
          <span class="block w-4 h-4 -ml-1 bg-red-700 rounded-full"></span> -->
        </div>
      </div>
      <div class="flex flex-col justify-around gap-2 text-sm">
        <ul class="flex flex-col gap-2">
          <li v-for="task in sortedTasks.slice(0, 5)" :key="task.id" class="relative">
            <div class="flex items-center gap-2 text-ellipsis">
              <p
                class="line-clamp-1 leading-tight"
                :class="{
                  'text-gray-400 line-through': task.is_done,
                }"
              >
                {{ task.name }}
              </p>
            </div>
          </li>
        </ul>
        <div v-if="!props.postit.tasks || props.postit.tasks.length === 0" class="italic text-gray-400">
          {{ t('employee.postits.noTasks') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
